import { calculateMonthData, DayData, getVisibilityRating } from './calendarData';
import { coreDirectionLine } from './coreDirection';

interface ICSLocation {
  name: string;
  latitude: number;
  longitude: number;
}

function formatDateForICS(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

function formatDateTimeForICS(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}${m}${d}T${h}${min}${s}Z`;
}

function escapeICSText(text: string): string {
  return text.replace(/[,;\\]/g, '\\$&').replace(/\n/g, '\\n');
}

function getStarRating(rating: number): string {
  return '⭐'.repeat(Math.min(rating, 10)) || '☆';
}

function createICSEvent(
  day: DayData & { month: number },
  location: ICSLocation,
  year: number,
  includeDirection: boolean,
): string {
  const rating = getVisibilityRating(day);
  const moonPercent = Math.round(day.moonPhase * 100);
  const starRating = getStarRating(rating);

  const eventDate = new Date(year, day.month, day.date);
  const startDate = formatDateForICS(eventDate);
  const endDate = new Date(eventDate);
  endDate.setDate(endDate.getDate() + 1);
  const endDateFormatted = formatDateForICS(endDate);

  const title = `🌌 Milky Way Photography ${starRating}`;

  const description =
    `🌌 OPTIMAL MILKY WAY PHOTOGRAPHY CONDITIONS` +
    `\\n\\n📍 LOCATION: ${location.name}` +
    `\\n📐 Coordinates: ${location.latitude.toFixed(4)}°, ${location.longitude.toFixed(4)}°` +
    `\\n\\n⭐ VISIBILITY: ${starRating} (${rating}/10)` +
    `\\n🌙 Moon Phase: ${moonPercent}%` +
    `\\n\\n⏰ TIMING` +
    `\\n🌌 Core Visibility: ${day.galacticCoreStart} - ${day.galacticCoreEnd}` +
    // Premium only, like the dark core time (ERI-2596).
    (includeDirection && coreDirectionLine(day) ? `\\n🧭 Point: ${coreDirectionLine(day)}` : '') +
    `\\n🌅 Sunrise: ${day.sunrise}` +
    `\\n🌇 Sunset: ${day.sunset}` +
    `\\n\\n💡 PHOTOGRAPHY TIPS` +
    `\\n• Arrive 30 minutes before core visibility starts` +
    `\\n• Check weather conditions separately` +
    `\\n• Find a location away from city lights` +
    `\\n• Allow 20-30 minutes for eyes to adjust to darkness` +
    `\\n• Use a red flashlight to preserve night vision` +
    `\\n\\n📱 Plan your shoot at: https://www.milkywayplanner.com` +
    `\\n\\nGenerated by MilkyWayPlanner.com`;

  const reminderTime = new Date(eventDate);
  reminderTime.setHours(20, 0, 0, 0);
  const alarmTime = formatDateTimeForICS(reminderTime);

  const priority = rating >= 8 ? '1' : rating >= 6 ? '5' : '9';

  return [
    'BEGIN:VEVENT',
    `UID:milkyway-${eventDate.getTime()}-${location.latitude}-${location.longitude}@milkywayplanner.com`,
    `DTSTAMP:${formatDateTimeForICS(new Date())}`,
    `CREATED:${formatDateTimeForICS(new Date())}`,
    `LAST-MODIFIED:${formatDateTimeForICS(new Date())}`,
    `DTSTART;VALUE=DATE:${startDate}`,
    `DTEND;VALUE=DATE:${endDateFormatted}`,
    `SUMMARY:${escapeICSText(title)}`,
    `DESCRIPTION:${escapeICSText(description)}`,
    `LOCATION:${escapeICSText(`${location.name} (${location.latitude.toFixed(4)}°, ${location.longitude.toFixed(4)}°)`)}`,
    `GEO:${location.latitude};${location.longitude}`,
    'CATEGORIES:Astrophotography,Milky Way,Photography,Planning',
    `PRIORITY:${priority}`,
    'STATUS:CONFIRMED',
    'TRANSP:TRANSPARENT',
    'CLASS:PUBLIC',
    'SEQUENCE:0',
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:Milky Way photography tonight! Visibility: ${starRating}`,
    `TRIGGER;VALUE=DATE-TIME:${alarmTime}`,
    'END:VALARM',
    'END:VEVENT',
  ].join('\r\n');
}

/**
 * Generate an ICS file content string for a full year at a given location.
 * Only includes nights with visibility rating >= minRating. The core direction
 * line is Premium only (ERI-2596): pass includeDirection for a subscriber.
 */
export function generateYearlyICS(
  location: ICSLocation,
  year: number,
  minRating = 4,
  { includeDirection = false }: { includeDirection?: boolean } = {},
): { icsContent: string; eventCount: number; filename: string } {
  const events: string[] = [];

  for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
    const monthData = calculateMonthData(location, monthIndex, year);
    for (const day of monthData) {
      if (getVisibilityRating(day) >= minRating) {
        events.push(createICSEvent({ ...day, month: monthIndex }, location, year, includeDirection));
      }
    }
  }

  const locationSlug = location.name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  const calendarTitle = `Milky Way Photography Calendar ${year} - ${location.name}`;

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//MilkyWayPlanner.com//Milky Way Photography Calendar V1.0//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeICSText(calendarTitle)}`,
    `X-WR-CALDESC:${escapeICSText('Optimal Milky Way photography dates with visibility ratings and detailed timing information')}`,
    'X-WR-TIMEZONE:UTC',
    'X-WR-RELCALID:' + Math.random().toString(36).substring(2, 11),
    'BEGIN:VTIMEZONE',
    'TZID:UTC',
    'BEGIN:STANDARD',
    'DTSTART:19700101T000000',
    'TZOFFSETFROM:+0000',
    'TZOFFSETTO:+0000',
    'TZNAME:UTC',
    'END:STANDARD',
    'END:VTIMEZONE',
    ...events,
    'END:VCALENDAR',
  ].join('\r\n');

  return {
    icsContent,
    eventCount: events.length,
    filename: `milky-way-calendar-${locationSlug}-${year}.ics`,
  };
}

/**
 * Trigger a browser download of an ICS file.
 */
export function downloadICSFile(icsContent: string, filename: string): void {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
